import PropTypes from 'prop-types'
import { createContext, useContext, useMemo, useState, useEffect } from 'react'

import { useAuthContext } from './AuthProvider'
import { useInitialData } from '../services/user'
import { COMPROMISED_SHIPMENTS_MAX } from '../common/constants'

const UserContext = createContext(null)

export const useUserContext = () => useContext(UserContext)

const UserProvider = ({ children }) => {
  const { user } = useAuthContext()
  const {
    isLoading,
    data: userData,
    isError,
    error,
  } = useInitialData(user.cognitoId)
  const [centerCode, setCenterCode] = useState()
  const [centerHideId, setCenterHideId] = useState()
  useEffect(() => {
    if (Boolean(userData?.data.defaultCentreCode)) {
      setCenterCode(userData?.data.defaultCentreCode)
    }
  }, [userData])

  useEffect(() => {
    if (centerCode && userData?.data?.centres) {
      const matchingCentre = userData.data.centres.find(
        centre => centre.id === centerCode,
      )
      if (matchingCentre) {
        setCenterHideId(matchingCentre.hideId)
      }
    }
  }, [centerCode, userData?.data?.centres])

  useEffect(() => {
    const webUserCode = userData?.data?.webUserCode
    if (webUserCode) {
      window.cttUserId = webUserCode
    }
    return () => {
      window.cttUserId = undefined
    }
  }, [userData?.data?.webUserCode])
  
  const userContext = useMemo(
    () => ({
      isLoading,
      clientCode: userData?.data?.clientCode,
      clientId: userData?.data?.clientId,
      centres: userData?.data?.centres ?? [],
      statusList: userData?.data?.statusList,
      defaultCentreCode: userData?.data.defaultCentreCode,
      compromisedShipments:  userData?.data?.centres?.length < 100
      ? userData?.data?.centres?.reduce(
        (sum, centre) => sum + (centre.compromisedShipments || 0),
        0
      )
      : COMPROMISED_SHIPMENTS_MAX+1,
      centerCode,
      setCenterCode,
      centerHideId,
      webUserCode: userData?.data.webUserCode,
      webUserEmail: userData?.data.webUserEmail,
      webUserName: userData?.data.webUserName,
      webUserPhone: userData?.data.webUserPhone,
      isFinancial: userData?.data.isFinancial,
      isMaster: userData?.data.isMaster,
      // BK/FR identity fields
      businessType: userData?.data?.businessType,
      webUserRole: userData?.data?.webUserRole,
      clientName: userData?.data?.clientName,
      fiscalCode: userData?.data?.fiscalCode,
      isBrokerOrFranchise: userData?.data?.isBrokerOrFranchise,
      // Effective permissions (pre-resolved by backend)
      canManageClients: userData?.data?.canManageClients,
      canManageUsers: userData?.data?.canManageUsers,
      canDeleteColaborador: userData?.data?.canDeleteColaborador,
      canDeleteGestor: userData?.data?.canDeleteGestor,
      canViewAllCenters: userData?.data?.canViewAllCenters,
      canResetPasswords: userData?.data?.canResetPasswords,
      canCreateAdminUsers: userData?.data?.canCreateAdminUsers,
      isError,
      error,
      auditData: {
        process_code: 'CLIENT-WEB',
        user_code: userData?.data?.webUserCode ?? '',
      },
    }),
    [
      isLoading,
      userData?.data?.clientCode,
      userData?.data?.clientId,
      userData?.data?.centres,
      userData?.data?.statusList,
      userData?.data?.defaultCentreCode,
      centerCode,
      setCenterCode,
      centerHideId,
      userData?.data?.webUserCode,
      userData?.data?.webUserEmail,
      userData?.data?.webUserName,
      userData?.data?.webUserPhone,
      userData?.data?.isFinancial,
      userData?.data?.isMaster,
      userData?.data?.businessType,
      userData?.data?.webUserRole,
      userData?.data?.clientName,
      userData?.data?.fiscalCode,
      userData?.data?.isBrokerOrFranchise,
      userData?.data?.canManageClients,
      userData?.data?.canManageUsers,
      userData?.data?.canDeleteColaborador,
      userData?.data?.canDeleteGestor,
      userData?.data?.canViewAllCenters,
      userData?.data?.canResetPasswords,
      userData?.data?.canCreateAdminUsers,
      isError,
      error,
    ],
  )

  return (
    <UserContext.Provider value={userContext}>{children}</UserContext.Provider>
  )
}

UserProvider.propTypes = {
  children: PropTypes.node,
}

export default UserProvider
